/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Grid, Typography, Typography2 } from '@coursera/cds-core';
import type { GridProps, Theme } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';

import _t from 'i18n!nls/xdp';

type Props = {
  imageSrc: string;
  domainName?: string;
  careerGrowthInPercentage?: number | null;
  jobOpeningsInUs?: number | null;
  medianEntryLevelSalary?: number | null;
  jobTitleForMedianEntryLevelSalary?: string | null;
  careerGrowthMessage?: string;
};

const styles = {
  dataContainer: (theme: Theme) => css`
    border-width: 0 2px 2px 2px;
    border-color: ${theme.palette.gray[400]};
    border-style: solid;
    border-radius: 0 0 8px 8px;
    padding: ${theme.spacing(16, 0)};
  `,
  withOneData: (theme: Theme) => css`
    // when there is only one data point, number and the text should be on the same line
    div {
      display: inline;
      vertical-align: middle;
      padding-right: ${theme.spacing(8)};
    }
  `,
  dataBlock: (theme: Theme) => css`
    padding: ${theme.spacing(0, 24)};

    & + & {
      margin-top: ${theme.spacing(16)};
    }
    ${theme.breakpoints.up('sm')} {
      & + & {
        margin-top: ${theme.spacing(0)};
      }

      /* stylelint-disable-next-line no-duplicate-selectors */
      & + & {
        border-left: 1px solid ${theme.palette.gray[300]};
      }
    }
  `,
  imageContainer: (allCornersRounded: boolean) => css`
    // override styles added by LazyImgix
    > div {
      width: 100%;
      height: auto;
      padding-bottom: unset;
      max-width: none;
    }

    img {
      max-width: none !important;
      max-height: none !important;
      width: 100%;
      height: auto;
      border-radius: ${allCornersRounded ? '8px' : '8px 8px 0 0'};
    }
  `,
};

const DataBox = ({
  imageSrc,
  careerGrowthInPercentage,
  jobOpeningsInUs,
  medianEntryLevelSalary,
  jobTitleForMedianEntryLevelSalary,
  domainName,
  careerGrowthMessage,
}: Props) => {
  const data = useMemo(() => {
    const values = [];
    if (medianEntryLevelSalary !== null && medianEntryLevelSalary !== undefined && jobTitleForMedianEntryLevelSalary) {
      values.push(
        <React.Fragment>
          <FormattedMessage
            message={_t('{salary} {medianUsSalary} for {jobTitle}')}
            medianUsSalary={<b>{_t('median U.S. salary')}</b>}
            salary={
              <Typography variant="h1semibold" component="div">
                <FormattedNumber
                  value={medianEntryLevelSalary}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="USD"
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                />
                +
              </Typography>
            }
            jobTitle={jobTitleForMedianEntryLevelSalary}
          />
          ¹
        </React.Fragment>
      );
    }
    if (
      jobOpeningsInUs !== null &&
      jobOpeningsInUs !== undefined &&
      (domainName || jobTitleForMedianEntryLevelSalary)
    ) {
      values.push(
        <React.Fragment>
          <FormattedMessage
            message={_t('{numberOfJobs} U.S. job openings in {domainName}')}
            numberOfJobs={
              <Typography variant="h1semibold" component="div">
                <FormattedNumber value={jobOpeningsInUs} />+
              </Typography>
            }
            domainName={jobTitleForMedianEntryLevelSalary || domainName}
          />
          ¹
        </React.Fragment>
      );
    }
    if (careerGrowthInPercentage !== null && careerGrowthInPercentage !== undefined) {
      values.push(
        <React.Fragment>
          <FormattedMessage
            message={careerGrowthMessage || _t('{percentage} of certificate graduates report career improvement')}
            percentage={
              <Typography variant="h1semibold" component="div">
                {/* eslint-disable-next-line react/style-prop-object */}
                <FormattedNumber value={careerGrowthInPercentage / 100} style="percent" />
              </Typography>
            }
          />
          {values.length === 0 ? '¹' : '²'}
        </React.Fragment>
      );
    }

    return values;
  }, [
    careerGrowthInPercentage,
    jobOpeningsInUs,
    medianEntryLevelSalary,
    domainName,
    jobTitleForMedianEntryLevelSalary,
    careerGrowthMessage,
  ]);

  const numberOfColumns = data.length > 0 ? ((12 / data.length) as GridProps['sm']) : 12;

  return (
    <div>
      <div css={styles.imageContainer(data.length === 0)}>
        <LazyImgix src={imageSrc} alt="" maxWidth={562} maxHeight={221} imgParams={{ fit: 'crop' }} />
      </div>
      {data.length > 0 && (
        <Grid container css={[styles.dataContainer, data.length === 1 ? styles.withOneData : undefined]}>
          {data.map((value, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Grid item xs={12} sm={numberOfColumns} css={styles.dataBlock} key={index}>
                <Typography2 variant="bodySecondary" component="div" color="supportText">
                  {value}
                </Typography2>
              </Grid>
            );
          })}
        </Grid>
      )}
    </div>
  );
};

export default DataBox;
